import { useEffect, useState } from "react";
import {
  Button,
  Datepicker,
  Label,
  Select,
  Modal,
  Textarea,
  TextInput,
} from "flowbite-react";
import { HiPlus } from "react-icons/hi";
import { NOTIFICATION_TYPES } from "../../../../utils";
import {
  useAuth,
  useToast,
  useExpansionOrganizational,
  useCurrency,
} from "../../../../hooks";

export const AddExpansionOrganizational = ({ accountId, onExpansionAdded }) => {
  const [openModal, setOpenModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [initialFormData, setInitialFormData] = useState({
    name: "",
    account_contact_id: "",
    additional_mrr: "",
    notes: "",
    activation_date: "",
  });
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const { apiClient } = useAuth();
  const showToast = useToast();
  const { currencySymbol } = useCurrency();
  const { createOrganizationalExpansion } =
    useExpansionOrganizational(accountId);

  useEffect(() => {
    const fetchContacts = async () => {
      if (!accountId) return;

      setContactsLoading(true);
      try {
        const response = await apiClient.get(
          `/accounts/${accountId}/contacts/`,
        );
        if (response.status === 200) {
          setContacts(response.data.results || response.data);
        }
      } catch (err) {
        console.error("Error fetching contacts:", err);
        showToast({
          content: "Error fetching contacts",
          type: NOTIFICATION_TYPES.error,
        });
      } finally {
        setContactsLoading(false);
      }
    };

    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.target);
      const formDataObject = Object.fromEntries(formData.entries());

      // Format the data according to API expectations
      const expansionData = {
        name: formDataObject.name,
        account_contact_id: formDataObject.account_contact_id,
        additional_mrr: formDataObject.additional_mrr,
        notes: formDataObject.notes,
        activation_date: selectedDate.toISOString(),
      };

      await createOrganizationalExpansion(expansionData);

      // Reset form and close modal
      setInitialFormData({
        name: "",
        account_contact_id: "",
        additional_mrr: "",
        notes: "",
        activation_date: "",
      });
      setOpenModal(false);

      if (onExpansionAdded) {
        onExpansionAdded();
      }
    } catch (error) {
      // Error is already handled in the hook
      console.error("Error creating expansion:", error);
    } finally {
      setLoading(false);
    }
  };

  const hasNoContacts = !contactsLoading && contacts.length === 0;

  return (
    <>
      <Button color="light" size="sm" onClick={() => setOpenModal(true)}>
        Add expansion organizational
        <HiPlus className="ml-2 h-5 w-5" />
      </Button>

      <Modal show={openModal} popup onClose={() => setOpenModal(false)}>
        <Modal.Header className="pl-5 pr-2 pt-3">
          <h3 className="text-xl font-medium text-gray-900 dark:text-white">
            Add expansion organizational
          </h3>
          <div className="text-xs font-normal leading-none text-gray-500 opacity-80">
            Add a new expansion organisational
          </div>
        </Modal.Header>
        <Modal.Body className="mt-3 space-y-5 px-5 pt-1">
          {hasNoContacts ? (
            <div className="py-8 text-center">
              <div className="mb-4">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-sm font-medium text-gray-900">
                No contacts available
              </h3>
              <p className="mb-4 text-sm text-gray-500">
                To create an expansion organizational, contacts must be synced
                from your HubSpot account first.
              </p>
              <p className="text-xs text-gray-400">
                Contacts are automatically synced from HubSpot and cannot be
                created manually in this system.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="space-y-5">
                <TextInput
                  id="name"
                  name="name"
                  placeholder="Give the expansion organizational a name"
                  defaultValue={initialFormData.name}
                  required
                />

                <div className="flex gap-4">
                  <div className="flex-1/3">
                    <Label htmlFor="activation_date">Activation Date</Label>
                    <Datepicker
                      id="activation_date"
                      defaultDate={selectedDate}
                      onSelectedDateChanged={setSelectedDate}
                      placeholder="Expected activation date"
                      minDate={new Date()}
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="additional_mrr">Additional MRR</Label>
                    <TextInput
                      id="additional_mrr"
                      type="number"
                      step="0.01"
                      placeholder={`Potential additional ${currencySymbol}MRR this expansion brings`}
                      name="additional_mrr"
                      defaultValue={initialFormData.additional_mrr}
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="account_contact_id">Account Contact</Label>
                  </div>
                  <Select
                    id="account_contact_id"
                    name="account_contact_id"
                    defaultValue={initialFormData.account_contact_id}
                    required
                    disabled={contactsLoading}
                  >
                    <option value="">
                      {contactsLoading
                        ? "Loading contacts..."
                        : "Select a contact..."}
                    </option>
                    {contacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name} -{" "}
                        {contact.email}
                      </option>
                    ))}
                  </Select>
                </div>

                <Textarea
                  id="notes"
                  rows={4}
                  placeholder="Add a note to provide context around this expansion"
                  name="notes"
                  defaultValue={initialFormData.notes}
                />

                <div className="flex w-full items-end justify-end">
                  <Button
                    type="submit"
                    color="blue"
                    disabled={loading}
                    loading={loading}
                  >
                    {loading ? "Adding..." : "Add"}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};
